import { PIX_KEY_TYPES } from "@caloteiros/shared";
import type { PixKeyType, UserDto } from "@caloteiros/shared";
import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/AppShell";
import { IconMoon, IconSun } from "../components/Icons";
import { Field, Segmented, Sheet, Spinner } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { pixTypeLabel } from "../lib/format";
import { usePeople } from "../lib/queries";
import { useTheme } from "../lib/theme";
import type { ThemePreference } from "../lib/theme";
import { useToast } from "../lib/toast";

export function ProfilePage(): ReactElement {
  const { user, logout, setUser } = useAuth();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const people = usePeople();

  const [name, setName] = useState(user?.name ?? "");
  const [pixType, setPixType] = useState<PixKeyType | "">("");
  const [pixKey, setPixKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // A chave Pix do dono da conta mora na "pessoa" dele — mesma coluna de todo
  // mundo, um lugar so para o app procurar.
  const me = people.data?.find((person) => person.isMe);

  useEffect(() => {
    if (!me) return;
    setPixType(me.pixKeyType ?? "");
    setPixKey(me.pixKey ?? "");
  }, [me]);

  useEffect(() => {
    if (user) setName(user.name);
  }, [user]);

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const updated = await api<UserDto>("/auth/me", {
        method: "PATCH",
        body: {
          name: name.trim(),
          pixKeyType: pixType === "" ? null : pixType,
          pixKey: pixKey.trim() === "" ? null : pixKey.trim(),
        },
      });
      setUser(updated);
      await people.refetch();
      toast.success("Perfil salvo.");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Não consegui salvar.");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (): Promise<void> => {
    try {
      await api<void>("/auth/me/password", {
        method: "POST",
        body: { currentPassword, newPassword },
      });
      toast.success("Senha trocada. As outras sessões foram derrubadas.");
      setPasswordOpen(false);
      setCurrentPassword("");
      setNewPassword("");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Não consegui trocar a senha.");
    }
  };

  return (
    <>
      <PageHeader title="Perfil" />

      <main className="app-content stack-lg">
        <section className="stack">
          <h2 className="section-title">Seus dados</h2>

          <Field label="Nome">
            <input
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
            />
          </Field>

          <Field label="E-mail" hint="O e-mail é o seu login e não muda por aqui.">
            <input className="input" value={user?.email ?? ""} disabled />
          </Field>

          <Field
            label="Tipo da chave Pix"
            hint="É essa chave que aparece para quem precisa te pagar."
          >
            <select
              className="select"
              value={pixType}
              onChange={(event) => setPixType(event.target.value as PixKeyType | "")}
            >
              <option value="">Sem chave Pix</option>
              {PIX_KEY_TYPES.map((option) => (
                <option key={option} value={option}>
                  {pixTypeLabel(option)}
                </option>
              ))}
            </select>
          </Field>

          {pixType !== "" ? (
            <Field label="Chave Pix">
              <input
                className="input"
                value={pixKey}
                onChange={(event) => setPixKey(event.target.value)}
                autoCapitalize="none"
                maxLength={140}
              />
            </Field>
          ) : null}

          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? <Spinner /> : "Salvar"}
          </button>
        </section>

        <section className="stack">
          <h2 className="section-title">Aparência</h2>
          <Segmented<ThemePreference>
            label="Tema"
            value={preference}
            onChange={setPreference}
            options={[
              { value: "light", label: "Claro" },
              { value: "dark", label: "Escuro" },
              { value: "system", label: "Sistema" },
            ]}
          />
          <p className="hint">
            {preference === "light" ? <IconSun width={14} height={14} style={{ display: "inline" }} /> : null}
            {preference === "dark" ? <IconMoon width={14} height={14} style={{ display: "inline" }} /> : null}{" "}
            No modo Sistema o app segue o tema do seu celular.
          </p>
        </section>

        <section className="stack">
          <h2 className="section-title">Conta</h2>
          <button
            type="button"
            className="btn btn-ghost btn-block"
            onClick={() => setPasswordOpen(true)}
          >
            Trocar senha
          </button>
          <button
            type="button"
            className="btn btn-danger btn-block"
            onClick={() => {
              void logout().then(() => navigate("/", { replace: true }));
            }}
          >
            Sair
          </button>
        </section>
      </main>

      <Sheet open={passwordOpen} onClose={() => setPasswordOpen(false)} title="Trocar senha">
        <div className="stack-lg">
          <Field label="Senha atual">
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </Field>
          <Field label="Nova senha" hint="Pelo menos 8 caracteres.">
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </Field>
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => void changePassword()}
          >
            Trocar senha
          </button>
        </div>
      </Sheet>
    </>
  );
}
